import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  DashboardUserInvite,
  Permission,
  Role,
} from "@xop/dashboard-shared";

import { api } from "../../../lib/api.js";

/**
 * All nine `/api/admin/users/*` mutations the Dashboard Users panel drives,
 * bundled into one hook so the panel body stays focused on rendering. Every
 * list-changing mutation invalidates `["admin","users"]`; the ones that don't
 * change the list (set-password, send/resend-invite) deliberately skip it and
 * surface their own inline / toast feedback. Return shape preserves the raw
 * react-query mutation objects, so callers keep using `.mutate` / `.mutateAsync`
 * / `.isPending` / `.isError` / `.error` exactly as before.
 */
export function useDashboardUserMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "users"] });

  const setRole = useMutation({
    mutationFn: async ({ id, role }: { id: number; role: Role }) =>
      api(`/api/admin/users/${id}/role`, {
        method: "PATCH",
        body: { dashboard_role: role },
      }),
    onSuccess: invalidate,
  });

  const setOverrides = useMutation({
    mutationFn: async ({ id, permissions }: { id: number; permissions: Permission[] }) =>
      api(`/api/admin/users/${id}/permissions`, {
        method: "PATCH",
        body: { permissions },
      }),
    onSuccess: invalidate,
  });

  const invite = useMutation({
    mutationFn: async (input: {
      email: string;
      name?: string;
      dashboard_role: Role;
      permission_overrides: Permission[];
    }) =>
      api<{ invite: DashboardUserInvite }>("/api/admin/users/invites", {
        method: "POST",
        body: input,
      }),
    onSuccess: invalidate,
  });

  const renameUser = useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) =>
      api(`/api/admin/users/${id}/profile`, { method: "PATCH", body: { name } }),
    onSuccess: invalidate,
  });

  const setPassword = useMutation({
    mutationFn: async ({ id, password }: { id: number; password: string }) =>
      api(`/api/admin/users/${id}/password`, { method: "POST", body: { password } }),
  });

  const setTwoFactor = useMutation({
    mutationFn: async ({ id, method }: { id: number; method: "email" | "disabled" }) =>
      api(`/api/admin/users/${id}/two-factor`, { method: "POST", body: { method } }),
    onSuccess: invalidate,
  });

  // (Re)send the first-time sign-in invite to an EXISTING user — the path for
  // a manually-created account that never got one (Amreek & co.). No list
  // change, so no invalidate; success/error surface via the toaster.
  const sendInvite = useMutation({
    mutationFn: async (id: number) =>
      api(`/api/admin/users/${id}/invite`, { method: "POST" }),
  });

  const deleteUser = useMutation({
    mutationFn: async (id: number) => api(`/api/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const cancelInvite = useMutation({
    mutationFn: async (id: number) =>
      api(`/api/admin/users/invites/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  // Re-send the sign-in notice for a pending invite. No list change,
  // so no invalidate — InviteRow shows inline ✓ / error feedback.
  const resendInvite = useMutation({
    mutationFn: async (id: number) =>
      api(`/api/admin/users/invites/${id}/resend`, { method: "POST" }),
  });

  return {
    setRole,
    setOverrides,
    invite,
    renameUser,
    setPassword,
    setTwoFactor,
    sendInvite,
    deleteUser,
    cancelInvite,
    resendInvite,
  };
}
